<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { devices, devices_status, devices_settings } from '../../../../stores';
	import TokenSelector from './TokenSelector.svelte';
	import type { DeviceReference } from '../../types/flow-types';
	import { real_id } from '../../../../helpers/interface_helpers';
	import { resolveDeviceInStore } from '../../helpers/resolveDeviceInStore';
	import { isSelectableForFlowDeviceCondition } from '../../helpers/whenConditionDeviceEligibility';
	import { isSelectableForFlowDeviceAction } from '../../helpers/doActionDeviceEligibility';

	export let selectedDevice: DeviceReference | undefined = undefined;
	export let mode: 'trigger' | 'action' = 'trigger';
	/** When true, limit to devices allowed in scene When→device conditions (and non-empty props when status/settings exist). */
	export let whenConditionFilter = false;

	const dispatch = createEventDispatcher<{ select: DeviceReference }>();

	const useDoActionFilter = () => mode === 'action' && !whenConditionFilter;

	$: availableDevices = (() => {
		const list = Object.values($devices || {})
			.filter((device) => device && device.id)
			.filter((device) => {
				if (whenConditionFilter) {
					return isSelectableForFlowDeviceCondition(
						device,
						$devices_status[real_id(device.id)],
						$devices_settings[real_id(device.id)],
					);
				}
				if (useDoActionFilter()) {
					return isSelectableForFlowDeviceAction(
						device,
						$devices_status[real_id(device.id)],
						$devices_settings[real_id(device.id)],
					);
				}
				return true;
			})
			.map((device) => ({
				title: device.name || device.id,
				value: device.id,
			}));

		const selId = selectedDevice?.id;
		const selCh = parseInt(String(selectedDevice?.channel ?? '0'), 10) || 0;
		if (selId) {
			const devResolved = $devices[selId] || resolveDeviceInStore($devices, selId, selCh);
			const inList = list.some(
				(o) => o.value === selId || (devResolved && o.value === devResolved.id),
			);
			if (!inList && devResolved) {
				const dev = devResolved;
				const statusKey = real_id(dev.id);
				const status = $devices_status[statusKey];
				const settings = $devices_settings[statusKey];
				if (whenConditionFilter && isSelectableForFlowDeviceCondition(dev, status, settings)) {
					return [
						...list,
						{ title: dev.name || selectedDevice?.name || selId, value: dev.id },
					];
				}
				if (useDoActionFilter()) {
					const selectable = isSelectableForFlowDeviceAction(dev, status, settings);
					if (selectable || real_id(selId) === real_id(dev.id)) {
						return [
							...list,
							{ title: dev.name || selectedDevice?.name || selId, value: dev.id },
						];
					}
				}
			}
		}

		return list;
	})();

	$: selectedOption = selectedDevice?.id
		? (() => {
				const ch = parseInt(String(selectedDevice.channel ?? '0'), 10) || 0;
				const resolved = resolveDeviceInStore($devices, selectedDevice.id, ch);
				const ids = [selectedDevice.id, resolved?.id].filter(Boolean) as string[];
				return availableDevices.find((opt) => ids.includes(String(opt.value)));
			})()
		: undefined;

	function handleDeviceChange(event: CustomEvent) {
		const selectedObj = event.detail;

		if (!selectedObj || !selectedObj.value) {
			dispatch('select', undefined);
			return;
		}

		const deviceId = selectedObj.value;
		const device = $devices[deviceId] || resolveDeviceInStore($devices, deviceId, 0);

		if (device) {
			dispatch('select', {
				id: device.id,
				name: device.name || device.id,
				channel: '0',
			});
		}
	}
</script>

<TokenSelector
	ariaLabel={$format('label_select_device', { default: 'Device' })}
	label={$format('label_select_device', { default: 'Device' })}
	options={availableDevices}
	value={selectedOption}
	on:change={handleDeviceChange}
/>

<style>
</style>
